import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/usePageTitle";
import AppLayout from "@/components/layout/app-layout";
import StatsCards from "@/components/dashboard/stats-cards";
import RecentActivity from "@/components/dashboard/recent-activity";
import { useQuery } from "@tanstack/react-query";
import CreateStationModal from "@/components/stations/create-station-modal";
import { Button } from "@/components/ui/button";
import { CompositeFavicon } from "@/components/ui/composite-favicon";
import { Plus, Radio } from "lucide-react";
import { Link } from "wouter";
import { useStations } from "@/hooks/useStations";

export default function Dashboard() {
  usePageTitle("Dashboard");
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const { data: stations = [], refetch: refetchStations } = useStations();

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AppLayout>
      {/* Dashboard Header */}
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="text-dashboard-title">Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your AI sales tools and stations</p>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards />

      {/* Stations Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">Your Stations</h2>
        </div>

        {stations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="text-center">
              <Radio className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No stations yet</h3>
              <p className="text-gray-600 mb-4">Visit Admin Settings to create your first station and start organizing your knowledge base</p>
              <Link href="/admin">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Go to Admin Settings
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {stations.map((station: any) => (
              <Link href={`/stations/${station.id}/vbr`} key={station.id}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer group">
                  <div className="flex items-center gap-4 h-12">
                    <div className="bg-primary-100 rounded-lg p-3 group-hover:bg-primary-200 transition-colors flex-shrink-0">
                      <CompositeFavicon 
                        faviconUrls={station.faviconUrls} 
                        size="sm"
                        fallbackIcon={<Radio className="h-6 w-6 text-primary-600" />}
                      />
                    </div>
                    <div className={`flex-1 min-w-0 ${station.description ? '' : 'flex items-center'}`}>
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{station.name}</h3>
                      {station.description && (
                        <p className="text-gray-600 text-sm truncate">{station.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <RecentActivity />
    </AppLayout>
  );
}
